import { useCallback, useEffect, useState } from "react";
import { doc, getDocFromServer, onSnapshot } from "firebase/firestore";
import { db } from "../Security/firebase-config";

const EMPTY = { team: null, loading: false, error: null };

// A selected-team pointer is not proof of membership. Verify the server roster
// first; cached/offline data cannot grant access to a newly selected team.
export function useTeamRecord(uid, teamId) {
  const identity = uid && teamId ? JSON.stringify([uid, teamId]) : null;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ identity: null, ...EMPTY });
  const refreshTeam = useCallback(() => {
    setState({ identity: null, ...EMPTY });
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!identity) return;
    let alive = true;
    let stopTeam;
    const teamRef = doc(db, "teams", teamId);
    const receive = (snapshot) => {
      if (!alive) return;
      setState({ identity, team: snapshot.exists() ? snapshot.data() : null, loading: false, error: null });
    };
    const fail = (error) => {
      if (alive) setState({ identity, ...EMPTY, error });
    };
    setState({ identity, ...EMPTY, loading: true });
    getDocFromServer(teamRef).then((snapshot) => {
      if (!alive) return;
      receive(snapshot);
      stopTeam = onSnapshot(teamRef, { includeMetadataChanges: true }, (next) => {
        if (!next.metadata.fromCache && !next.metadata.hasPendingWrites) receive(next);
      }, fail);
    }, fail);
    return () => { alive = false; stopTeam?.(); };
  }, [uid, teamId, identity, attempt]);

  const current = !identity ? EMPTY : state.identity === identity ? state : { ...EMPTY, loading: true };
  return {
    ...current,
    isActiveTeamMember: Boolean(uid && current.team && Array.isArray(current.team.members) && current.team.members.includes(uid)),
    refreshTeam,
  };
}
